import { GuidEmpty } from 'mbs-ui-shared@constants';
import AdministratorInCamelCase from 'mbs-ui-shared@models/AdministratorInCamelCase';
import { ScheduleStepValue } from 'mbs-ui-shared@models/backup/schedule-models';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { AdvancedOptionsFlags, AdvancedOptionsStepValue } from 'wizards/models/advanced-options-models';
import { BackupFileFormModel } from 'wizards/models/backupFile/backup-form-model';
import { DestinationPlusRetentionStepValue } from 'wizards/models/combined/destination-plus-retention-model';
import { CompressionEncryptionStepValue } from 'wizards/models/compression-encription-models';
import { IBBFormModel } from 'wizards/models/ibb/ibb-form-model';
import { WhatBackupStepValue } from 'wizards/models/what-backup-models';
import { WhatBackupTreeStepValue } from 'wizards/models/what-backup-tree-model';

export enum CompanyCreateResult {
  CreateAndContinue = 'createAndContinue',
  CreateAndExit = 'createAndExit'
}

export type CompanyNameStepValue = {
  name: string;
};

export const CompanyNameStepDefaultValue: CompanyNameStepValue = {
  name: ''
};

export type CompanyAdministratorsStepValue = {
  administrators: AdministratorInCamelCase[];
};

export enum CompanyAdministratorPermissionsOption {
  Preset = 'preset',
  Manual = 'manual',
  All = 'all'
}

export const CompanyAdministratorsStepDefaultValue: CompanyAdministratorsStepValue = {
  administrators: []
};

export type CompanyBackupTemplateField = 'source' | 'schedule' | 'compression' | 'encryption';

export type CompanyBackupTemplateDefinition = {
  id: string;
  isImageBased: boolean;
  isLinux: boolean;
  isMac: boolean;
  // Drives which path validators the plan steps apply, so it has to travel with the template
  isUnix: boolean;
};

export type CompanyBackupTemplate = CompanyBackupTemplateDefinition & {
  advancedOptions: AdvancedOptionsStepValue;
  advancedOptionsFlags: AdvancedOptionsFlags;
  applyTo: string;
  compressionEncryptionStep: CompressionEncryptionStepValue;
  create: boolean;
  // Kept in the plan wizard's own shapes so a template converts into a plan without a translation layer
  destinationPlusRetention: DestinationPlusRetentionStepValue;
  name: string;
  planType: PlanTypes;
  schedule: string;
  scheduleStep: ScheduleStepValue;
  source: string;
  // Only one of the two applies, mirroring the plan form models: image-based uses volumes, file plans use paths
  whatBackupStep?: WhatBackupStepValue;
  whatBackupTreeStep?: WhatBackupTreeStepValue;
};

export const getTemplatePlanType = (isImageBased: boolean): PlanTypes => (isImageBased ? PlanTypes.BackupDiskImagePlan : PlanTypes.Plan);

// Defaults come from the product's own plan form models rather than being restated here
const TemplateDefaultIncludeFolders: Partial<Record<string, string[]>> = {
  'files-linux': ['/home', '/etc'],
  'files-mac': ['/Users'],
  'files-windows': ['C:\\Users']
};

export const getTemplatePlanDefaults = (
  definition: CompanyBackupTemplateDefinition
): Pick<
  CompanyBackupTemplate,
  | 'advancedOptions'
  | 'advancedOptionsFlags'
  | 'compressionEncryptionStep'
  | 'destinationPlusRetention'
  | 'scheduleStep'
  | 'whatBackupStep'
  | 'whatBackupTreeStep'
> => {
  const { id, isImageBased, isUnix } = definition;
  const formModel = isImageBased ? new IBBFormModel(true, isUnix) : new BackupFileFormModel(true, isUnix);

  return {
    advancedOptions: formModel.advancedOptionsStep,
    advancedOptionsFlags: formModel.advancedOptionsFlags,
    compressionEncryptionStep: formModel.compressionEncryptionStep,
    destinationPlusRetention: {
      retentionPolicyStep: formModel.retentionPolicyStep,
      valid: false,
      whereBackupStep: { ConnectionID: GuidEmpty, HybridID: GuidEmpty, IsHybridBackup: false, valid: false }
    },
    scheduleStep: { ...formModel.scheduleStep, ScheduleType: 'noschedule' },
    whatBackupStep: isImageBased ? (formModel as IBBFormModel).whatBackupStep : undefined,
    whatBackupTreeStep: isImageBased
      ? undefined
      : { ...(formModel as BackupFileFormModel).whatBackupTreeStep, includeFolders: [...(TemplateDefaultIncludeFolders[id] ?? [])] }
  };
};

export const CompanyBackupTemplateDefinitions: CompanyBackupTemplateDefinition[] = [
  { id: 'files-mac', isImageBased: false, isLinux: false, isMac: true, isUnix: true },
  { id: 'files-linux', isImageBased: false, isLinux: true, isMac: false, isUnix: true },
  { id: 'files-windows', isImageBased: false, isLinux: false, isMac: false, isUnix: false },
  { id: 'image-windows', isImageBased: true, isLinux: false, isMac: false, isUnix: false }
];

export type CompanyBackupTemplatesStepValue = {
  templates: CompanyBackupTemplate[];
};

export const CompanyBackupTemplatesStepDefaultValue: CompanyBackupTemplatesStepValue = {
  templates: []
};

export enum CompanyDownloadProduct {
  Backup = 'backup',
  Rmm = 'rmm',
  Connect = 'connect'
}

export type CompanyDownloadStepValue = {
  product: CompanyDownloadProduct;
};

export const CompanyDownloadStepDefaultValue: CompanyDownloadStepValue = {
  product: CompanyDownloadProduct.Backup
};

export const CompanyDownloadIcons = {
  linux: 'fa fa-linux',
  mac: 'fa fa-apple',
  windows: 'ico ico-windows'
};
